import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'ms-auth-forwarder',
  imports: [],
  templateUrl: './auth-forwarder.component.html',
})
export class AuthForwarderComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private router = inject(Router);

  public async ngOnInit(): Promise<void> {
    // The browser can restore /authenticate from back-forward cache when the
    // user hits back on the Auth0 hosted login page, in which case ngOnInit
    // does not fire and the sentinel check below never runs. Listen for
    // pageshow with event.persisted to detect that restore and bounce out.
    window.addEventListener('pageshow', this.handlePageShow);

    // Consume the intent up front so it can never outlive the visit that set
    // it, even when the isAuthenticated short-circuit below skips past the
    // intent gate. Only a Sign in button click warrants forwarding to Auth0;
    // any other arrival at /authenticate bounces back to /sign-in (DEV-2597).
    const intent = sessionStorage.getItem('ms.auth.intent');
    sessionStorage.removeItem('ms.auth.intent');

    const isAuthenticated = await firstValueFrom(this.authService.isAuthenticated$);

    // Already Auth0-authenticated: skip the redundant hosted-login round trip and go
    // straight into the app so the guards can re-evaluate the current session state.
    if (isAuthenticated) {
      await this.router.navigate(['/dashboard']);
      return;
    }

    if (intent !== 'sign-in-click') {
      await this.router.navigate(['/sign-in']);
      return;
    }

    try {
      await firstValueFrom(this.authService.loginWithRedirect({
        appState: { target: '/dashboard' },
      }));
    } catch (error) {
      console.error('AuthForwarderComponent: loginWithRedirect failed', error);
      await this.router.navigate(['/sign-in']);
    }
  }

  public ngOnDestroy(): void {
    window.removeEventListener('pageshow', this.handlePageShow);
  }

  private handlePageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) {
      this.router.navigate(['/sign-in']);
    }
  };
}
